import React, { createContext, useContext, useEffect, useState } from 'react';
import { moaniPriceService } from '../services/MoaniPriceService';

const MoaniPriceContext = createContext();

/**
 * MOANI Price Provider
 * Manages MOANI price globally and fetches on login
 */
export function MoaniPriceProvider({ children }) {
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Initialize price on mount
  useEffect(() => {
    initializePrice();
  }, []);

  const initializePrice = async () => {
    try {
      setLoading(true);
      
      // Get cached price first for immediate display
      const cachedPrice = moaniPriceService.getPriceSync();
      if (cachedPrice !== null) {
        setPrice(cachedPrice);
        setLoading(false);
      }

      // Fetch fresh price
      const freshPrice = await moaniPriceService.getPrice();
      setPrice(freshPrice);
      setLastUpdated(new Date());
    } catch (error) {
      console.warn('Failed to initialize MOANI price:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshPrice = async () => {
    try {
      setLoading(true);
      const freshPrice = await moaniPriceService.refreshPrice();
      setPrice(freshPrice);
      setLastUpdated(new Date());
    } catch (error) {
      console.warn('Failed to refresh MOANI price:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateUSDValue = (moaniAmount) => {
    return moaniPriceService.calculateUSDValue(moaniAmount, price);
  };

  const value = {
    price,
    loading,
    lastUpdated,
    refreshPrice,
    calculateUSDValue,
    initializePrice
  };

  return (
    <MoaniPriceContext.Provider value={value}>
      {children}
    </MoaniPriceContext.Provider>
  );
}

/**
 * Hook to use MOANI price context
 */
export function useMoaniPrice() {
  const context = useContext(MoaniPriceContext);
  if (!context) {
    throw new Error('useMoaniPrice must be used within a MoaniPriceProvider');
  }
  return context;
}

export default MoaniPriceContext;
